import {
  Bottom,
  Card,
  CardDots,
  CartTitle,
  DeleteButton,
  DeleteIcon,
  Item,
  Name,
  StarButton,
  StarIcon,
  Top,
} from './PaymentMethodItem.styled';
import {
  ButtonsWrapper,
  CancelButton,
  ModalCloseButton,
  ModalCloseIcon,
  ModalContent,
  ModalExclamationIcon,
  ModalWrapper,
  Title,
} from '../../shared/DeleteConfirmationModal/DeleteConfirmationModal.styled';
import {
  Description,
  Wrapper,
} from '../../components/ProductDetail/ProductDetail.styled';
import {
  deletePaymentMethod,
  setPrimary,
  setPaymentMethods,
} from '../../store/paymentMethods/paymentMethodsSlice';
import {
  faExclamationCircle,
  faStar,
  faTimes,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import {
  removePaymentMethod,
  setDefaultMethod,
  retreivePaymentMethods,
} from '../../api/paymentMethods';

import { Button } from '../../shared';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Image from 'next/image';
import type { PaymentMethodsType } from '../../types/paymentMethods';
import { faStar as faStarEmpty } from '@fortawesome/free-regular-svg-icons';
import { useDispatch } from 'react-redux';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { handleToastAsyncAction } from '../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';

interface Props extends PaymentMethodsType {
  condensed: boolean;
}

export const PaymentMethodItem = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const icon = props.primary ? faStar : (faStarEmpty as IconProp);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

  const handleConfirmModal = async () => {
    setConfirmDeleteModal(!confirmDeleteModal);
  };

  const handleDelete = async (event: FormEvent<HTMLElement>) => {
    event.preventDefault();

    await handleToastAsyncAction(() => removePaymentMethod({ id: props.id }), {
      onSuccessMessage: t('payment.delete.toastify.success'),
      onErrorMessage: t('payment.delete.toastify.error'),
      dispatchAction: async () => {

        dispatch(deletePaymentMethod({ id: props.id }));

        try {
          const updatedMethods = await retreivePaymentMethods();
          dispatch(setPaymentMethods(updatedMethods));

          const hasPrimary = updatedMethods.results.some((method: any) => method.primary);

          if (!hasPrimary && updatedMethods.results.length > 0) {
            const firstMethod = updatedMethods.results[0];
            try {
              await setDefaultMethod({ id: firstMethod.id });
              const finalMethods = await retreivePaymentMethods();
              dispatch(setPaymentMethods(finalMethods));
            } catch (error) {
              console.error('Error setting first method as primary:', error);
            }
          }
        } catch (error) {
          console.error('Error reloading payment methods:', error);
        }
      },
      errorLogger: (error) => {
        console.error('Error deleting payment method:', error);
        Sentry.captureException(error);
      },
    });
  };

  const handlePrimary = async (event: FormEvent<HTMLElement>) => {
    event.preventDefault();

    await handleToastAsyncAction(() => setDefaultMethod({ id: props.id }), {
      onSuccessMessage: `${props.brand.charAt(0).toLocaleUpperCase() + props.brand.slice(1)
        } ${props.last4} ${t('payment.setPrimary.toastify.success')}`,
      onErrorMessage: t('payment.setPrimary.toastify.error'),
      dispatchAction: () => dispatch(setPrimary({ id: props.id })),
      errorLogger: (error) => {
        console.error('Error setting primary payment method:', error);
        Sentry.captureException(error);
      },
    });
  };

  return (
    <Item condensed={props.condensed}>
      <Top>
        <StarButton onClick={handlePrimary}>
          <StarIcon icon={icon} width={23} primary={props.primary} />
        </StarButton>
        <Name>{`${props.brand} ${props.last4}`}</Name>
      </Top>
      <CartTitle>{t('payment.cardNumber.title')}</CartTitle>
      <Card>
        <CardDots>•••• •••••</CardDots>
        {props.last4}
      </Card>
      <Bottom>
        <Image
          src={props.brandImage}
          alt={props.brandImage}
          width={30}
          height={23}
          objectFit="contain"
        />
        <DeleteButton onClick={handleConfirmModal}>
          <DeleteIcon icon={faTrashCan} width={14} />
        </DeleteButton>
        {confirmDeleteModal ? (
          <ModalWrapper>
            <ModalContent>
              <ModalCloseButton onClick={handleConfirmModal}>
                <ModalCloseIcon icon={faTimes} />
              </ModalCloseButton>
              <Wrapper>
                <ModalExclamationIcon icon={faExclamationCircle} />
                <Title>{t('payment.deleteModal.title')}</Title>
                <Description>
                  {t('payment.deleteModal.description')}
                </Description>
                <ButtonsWrapper>
                  <Button
                    title={t('payment.deleteModal.cta.delete')}
                    onClick={handleDelete}
                  />
                  <CancelButton
                    title={t('payment.deleteModal.cta.cancel')}
                    type="reset"
                    onClick={() => setConfirmDeleteModal(false)}
                  />
                </ButtonsWrapper>
              </Wrapper>
            </ModalContent>
          </ModalWrapper>
        ) : null}
      </Bottom>
    </Item>
  );
};
